import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { getListState, setListState } from "../../lib/listStateStore";

const CONTAINER_ID = "app-scroll-container";

// Remembers and restores the main content scroll position per route, so
// returning to a list (after opening a detail page or visiting another
// section) lands you exactly where you left off. List filters/search/page
// are remembered separately via useListState. Nothing is ever cleared on
// navigation — state persists for the whole browser-tab session.
export function SectionStateGuard() {
  const location = useLocation();
  const key = `scroll:${location.pathname}`;

  useLayoutEffect(() => {
    const target = getListState(key, 0);
    let frame = 0;
    let saveTimer = 0;
    let el = null;
    let tries = 0;
    let settled = 0;

    // Save a settled scroll position after the user stops scrolling. The
    // browser's reset-to-top that fires during navigation never persists:
    // its pending save is cancelled by this effect's cleanup (which runs the
    // instant the route changes, well within the debounce window).
    const onScroll = () => {
      const top = el.scrollTop;
      clearTimeout(saveTimer);
      saveTimer = setTimeout(() => setListState(key, top), 250);
    };

    // The scroll container lives in AppShell, which can mount a moment after
    // this effect runs on a fresh load (auth resolves asynchronously). Poll
    // for it, then keep re-applying the saved position until it holds stable
    // for several consecutive frames — riding through the skeleton→data
    // reflow that would otherwise clamp us back to the top.
    const tick = () => {
      if (!el) {
        el = document.getElementById(CONTAINER_ID);
        if (!el) {
          if (tries++ < 180) frame = requestAnimationFrame(tick);
          return;
        }
        el.addEventListener("scroll", onScroll, { passive: true });
      }
      if (target <= 0) return;
      const maxScroll = el.scrollHeight - el.clientHeight;
      if (maxScroll >= target) {
        el.scrollTop = target;
        settled = Math.abs(el.scrollTop - target) <= 2 ? settled + 1 : 0;
      } else {
        settled = 0;
      }
      if (settled >= 8 || tries++ > 180) return;
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(saveTimer);
      if (el) el.removeEventListener("scroll", onScroll);
    };
  }, [key]);

  return null;
}
